// Interface icons: Flaticon UIcons (rounded), licensed under Seeklab's Flaticon Premium subscription
// (no attribution required). Components keep the names the app has always used, so call sites read
// `<Flag size={14} />`; each renders the mapped glyph from the icon font as a square, centered box.
// Weight follows the familiar icon props: a `fill` (other than none) gives the solid glyph in that color,
// a `strokeWidth` of 2.5 or more gives the bold glyph; everything else is regular.
import { forwardRef } from 'react';
import clsx from 'clsx';

export interface IconProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  size?: number | string;
  color?: string;
  fill?: string;
  strokeWidth?: number | string;
}

export type IconComponent = React.ForwardRefExoticComponent<IconProps & React.RefAttributes<HTMLElement>>;

function uicon(glyph: string, name: string): IconComponent {
  const C = forwardRef<HTMLElement, IconProps>(function UIcon({ size = 24, color, fill, strokeWidth, className, style, ...rest }, ref) {
    const filled = !!fill && fill !== 'none' && fill !== 'transparent';
    const weight = filled ? 'sr' : Number(strokeWidth) >= 2.5 ? 'br' : 'rr';
    return (
      <i
        ref={ref}
        aria-hidden="true"
        {...rest}
        className={clsx('uicon', `fi-${weight}-${glyph}`, className)}
        style={{ fontSize: size, color: filled ? fill : color, ...style }}
      />
    );
  });
  C.displayName = name;
  return C;
}

export const Activity = uicon('pulse', 'Activity');
export const AlarmClock = uicon('alarm-clock', 'AlarmClock');
export const AlarmClockOff = uicon('alarm-snooze', 'AlarmClockOff');
export const AlignLeft = uicon('align-left', 'AlignLeft');
export const Apps = uicon('apps', 'Apps');
export const Archive = uicon('archive', 'Archive');
export const ArchiveRestore = uicon('box-open', 'ArchiveRestore');
export const ArrowDown = uicon('arrow-small-down', 'ArrowDown');
export const ArrowDownUp = uicon('sort-alt', 'ArrowDownUp');
export const ArrowLeft = uicon('arrow-small-left', 'ArrowLeft');
export const ArrowRight = uicon('arrow-small-right', 'ArrowRight');
export const ArrowUp = uicon('arrow-small-up', 'ArrowUp');
export const AtSign = uicon('at', 'AtSign');
export const Ban = uicon('ban', 'Ban');
export const Bell = uicon('bell', 'Bell');
export const BellOff = uicon('bell-slash', 'BellOff');
export const Bold = uicon('bold', 'Bold');
export const Bolt = uicon('bolt', 'Bolt');
export const Book = uicon('book', 'Book');
export const Bookmark = uicon('bookmark', 'Bookmark');
export const BookOpen = uicon('book-open-cover', 'BookOpen');
export const Bot = uicon('robot', 'Bot');
export const Briefcase = uicon('briefcase', 'Briefcase');
export const Bug = uicon('bug', 'Bug');
export const Building2 = uicon('building', 'Building2');
export const Calendar = uicon('calendar', 'Calendar');
export const CalendarCheck = uicon('calendar-check', 'CalendarCheck');
export const CalendarClock = uicon('calendar-clock', 'CalendarClock');
export const CalendarDays = uicon('calendar-days', 'CalendarDays');
export const CalendarPlus = uicon('calendar-plus', 'CalendarPlus');
export const CalendarRange = uicon('calendar-lines', 'CalendarRange');
export const CalendarSync = uicon('calendar-swap', 'CalendarSync');
export const Camera = uicon('camera', 'Camera');
export const CaretRight = uicon('caret-right', 'CaretRight');
export const CaseSensitive = uicon('letter-case', 'CaseSensitive');
export const ChartGantt = uicon('chart-gantt', 'ChartGantt');
export const ChartPie = uicon('chart-pie', 'ChartPie');
export const Check = uicon('check', 'Check');
export const CheckCheck = uicon('check-double', 'CheckCheck');
export const CheckSquare = uicon('checkbox', 'CheckSquare');
export const ChevronDown = uicon('angle-small-down', 'ChevronDown');
export const ChevronDownSquare = uicon('caret-square-down', 'ChevronDownSquare');
export const ChevronLeft = uicon('angle-small-left', 'ChevronLeft');
export const ChevronRight = uicon('angle-small-right', 'ChevronRight');
export const ChevronUp = uicon('angle-small-up', 'ChevronUp');
export const ChevronsLeft = uicon('angle-double-small-left', 'ChevronsLeft');
export const ChevronsRight = uicon('angle-double-small-right', 'ChevronsRight');
export const CircleAlert = uicon('exclamation', 'CircleAlert');
export const CircleCheck = uicon('check-circle', 'CircleCheck');
export const CircleCheckBig = uicon('check-circle', 'CircleCheckBig');
export const CircleDashed = uicon('circle-dashed', 'CircleDashed');
export const CircleDot = uicon('dot-circle', 'CircleDot');
export const CircleEllipsis = uicon('circle-ellipsis', 'CircleEllipsis');
export const CircleUserRound = uicon('circle-user', 'CircleUserRound');
export const Clapperboard = uicon('clapperboard', 'Clapperboard');
export const Clipboard = uicon('clipboard', 'Clipboard');
export const ClipboardList = uicon('clipboard-list', 'ClipboardList');
export const Clock = uicon('clock', 'Clock');
export const Code = uicon('code-simple', 'Code');
export const Coffee = uicon('coffee', 'Coffee');
export const Columns3 = uicon('columns-3', 'Columns3');
export const Copy = uicon('copy', 'Copy');
export const CopyPlus = uicon('duplicate', 'CopyPlus');
export const CornerDownLeft = uicon('arrow-turn-down-left', 'CornerDownLeft');
export const CornerDownRight = uicon('arrow-turn-down-right', 'CornerDownRight');
export const CornerLeftUp = uicon('arrow-turn-left-up', 'CornerLeftUp');
export const Crosshair = uicon('location-crosshairs', 'Crosshair');
export const Crown = uicon('crown', 'Crown');
export const Diamond = uicon('diamond', 'Diamond');
export const DollarSign = uicon('dollar', 'DollarSign');
export const Download = uicon('download', 'Download');
export const Ellipsis = uicon('menu-dots', 'Ellipsis');
export const ExternalLink = uicon('arrow-up-right-from-square', 'ExternalLink');
export const Eye = uicon('eye', 'Eye');
export const EyeOff = uicon('eye-crossed', 'EyeOff');
export const File = uicon('file', 'File');
export const FileArchive = uicon('file-zipper', 'FileArchive');
export const FileExport = uicon('file-export', 'FileExport');
export const FileAudio = uicon('file-audio', 'FileAudio');
export const FileImage = uicon('file-image', 'FileImage');
export const FilePlus2 = uicon('add-document', 'FilePlus2');
export const FileSpreadsheet = uicon('file-spreadsheet', 'FileSpreadsheet');
export const FileText = uicon('document', 'FileText');
export const FileVideo = uicon('file-video', 'FileVideo');
export const Flag = uicon('flag', 'Flag');
export const Flame = uicon('flame', 'Flame');
export const FoldHorizontal = uicon('compress-alt', 'FoldHorizontal');
export const Folder = uicon('folder', 'Folder');
export const FolderOpen = uicon('folder-open', 'FolderOpen');
export const Gem = uicon('gem', 'Gem');
export const Gift = uicon('gift', 'Gift');
export const GitPullRequestArrow = uicon('code-pull-request', 'GitPullRequestArrow');
export const Globe = uicon('globe', 'Globe');
export const GraduationCap = uicon('graduation-cap', 'GraduationCap');
export const Grid3x3 = uicon('grid', 'Grid3x3');
export const GripVertical = uicon('grip-dots-vertical', 'GripVertical');
export const Hash = uicon('hastag', 'Hash');
export const Heading1 = uicon('h1', 'Heading1');
export const Heading2 = uicon('h2', 'Heading2');
export const Heart = uicon('heart', 'Heart');
export const Hourglass = uicon('hourglass', 'Hourglass');
export const House = uicon('home', 'House');
export const Image = uicon('picture', 'Image');
export const Inbox = uicon('inbox', 'Inbox');
export const Info = uicon('info', 'Info');
export const Italic = uicon('italic', 'Italic');
export const KeyRound = uicon('key', 'KeyRound');
export const Keyboard = uicon('keyboard', 'Keyboard');
export const Layers = uicon('layers', 'Layers');
export const LayoutDashboard = uicon('dashboard-panel', 'LayoutDashboard');
export const LayoutGrid = uicon('apps', 'LayoutGrid');
export const LayoutTemplate = uicon('browser-ui', 'LayoutTemplate');
export const Lightbulb = uicon('bulb', 'Lightbulb');
export const Link = uicon('link', 'Link');
export const Link2 = uicon('link-alt', 'Link2');
export const List = uicon('list', 'List');
export const ListChecks = uicon('list-check', 'ListChecks');
export const ListFilter = uicon('bars-filter', 'ListFilter');
export const ListOrdered = uicon('sort-numeric-down', 'ListOrdered');
export const ListTodo = uicon('list-check', 'ListTodo');
export const LoaderCircle = uicon('spinner', 'LoaderCircle');
export const Lock = uicon('lock', 'Lock');
export const LogOut = uicon('sign-out-alt', 'LogOut');
export const Mail = uicon('envelope', 'Mail');
export const MailOpen = uicon('envelope-open', 'MailOpen');
export const Map = uicon('map', 'Map');
export const Maximize2 = uicon('expand-arrows-alt', 'Maximize2');
export const Megaphone = uicon('megaphone', 'Megaphone');
export const MenuBurger = uicon('menu-burger', 'MenuBurger');
export const MessageCircle = uicon('comment', 'MessageCircle');
export const MessageSquare = uicon('comment-alt', 'MessageSquare');
export const MessageSquareOff = uicon('comment-slash', 'MessageSquareOff');
export const MessageSquareReply = uicon('reply-all', 'MessageSquareReply');
export const MessagesSquare = uicon('comments', 'MessagesSquare');
export const Mic = uicon('microphone', 'Mic');
export const Minimize2 = uicon('compress', 'Minimize2');
export const Monitor = uicon('computer', 'Monitor');
export const Moon = uicon('moon', 'Moon');
export const MoreHorizontal = uicon('menu-dots', 'MoreHorizontal');
export const Music = uicon('music', 'Music');
export const NotebookPen = uicon('journal-alt', 'NotebookPen');
export const Orbit = uicon('planet-ringed', 'Orbit');
export const Package = uicon('box', 'Package');
export const Palette = uicon('palette', 'Palette');
export const PanelLeft = uicon('sidebar', 'PanelLeft');
export const PanelLeftClose = uicon('sidebar', 'PanelLeftClose');
export const PanelLeftOpen = uicon('sidebar', 'PanelLeftOpen');
export const PanelRightClose = uicon('sidebar-flip', 'PanelRightClose');
export const PanelRightOpen = uicon('sidebar-flip', 'PanelRightOpen');
export const Paperclip = uicon('clip', 'Paperclip');
export const Pause = uicon('pause', 'Pause');
export const PenTool = uicon('pen-nib', 'PenTool');
export const Pencil = uicon('pencil', 'Pencil');
export const Percent = uicon('percentage', 'Percent');
export const Pin = uicon('thumbtack', 'Pin');
export const Phone = uicon('phone-call', 'Phone');
export const Play = uicon('play', 'Play');
export const Plug = uicon('plug-connection', 'Plug');
export const Plus = uicon('plus-small', 'Plus');
export const Presentation = uicon('presentation', 'Presentation');
export const Quote = uicon('quote-right', 'Quote');
export const Rocket = uicon('rocket', 'Rocket');
export const RotateCcw = uicon('rotate-left', 'RotateCcw');
export const Search = uicon('search', 'Search');
export const Send = uicon('paper-plane', 'Send');
export const SendHorizontal = uicon('paper-plane-top', 'SendHorizontal');
export const Settings = uicon('settings', 'Settings');
export const Settings2 = uicon('settings-sliders', 'Settings2');
export const Shapes = uicon('category', 'Shapes');
export const Share2 = uicon('share', 'Share2');
export const Sheet = uicon('table-list', 'Sheet');
export const SlidersHorizontal = uicon('settings-sliders', 'SlidersHorizontal');
export const Shield = uicon('shield', 'Shield');
export const ShoppingCart = uicon('shopping-cart', 'ShoppingCart');
export const Smile = uicon('smile', 'Smile');
export const SmilePlus = uicon('smile-plus', 'SmilePlus');
export const Sparkles = uicon('sparkles', 'Sparkles');
export const Split = uicon('split', 'Split');
export const Square = uicon('square', 'Square');
export const SquareKanban = uicon('square-kanban', 'SquareKanban');
export const SquarePen = uicon('edit', 'SquarePen');
export const Star = uicon('star', 'Star');
export const StarOff = uicon('star', 'StarOff');
export const Strikethrough = uicon('strikethrough', 'Strikethrough');
export const Sun = uicon('sun', 'Sun');
export const Tag = uicon('label', 'Tag');
export const Tags = uicon('tags', 'Tags');
export const Target = uicon('target', 'Target');
export const TextCursorInput = uicon('input-text', 'TextCursorInput');
export const Timer = uicon('stopwatch', 'Timer');
export const ToggleLeft = uicon('toggle-on', 'ToggleLeft');
export const Trash2 = uicon('trash', 'Trash2');
export const TriangleAlert = uicon('triangle-warning', 'TriangleAlert');
export const Trophy = uicon('trophy', 'Trophy');
export const Truck = uicon('truck-side', 'Truck');
export const Type = uicon('text', 'Type');
export const Underline = uicon('underline', 'Underline');
export const Unlink = uicon('link-slash', 'Unlink');
export const Upload = uicon('upload', 'Upload');
export const UploadCloud = uicon('cloud-upload-alt', 'UploadCloud');
export const User = uicon('user', 'User');
export const UserCheck = uicon('user-check', 'UserCheck');
export const UserCrown = uicon('user-crown', 'UserCrown');
export const UserMinus = uicon('user-minus', 'UserMinus');
export const UserShield = uicon('user-shield', 'UserShield');
export const UserTag = uicon('user-tag', 'UserTag');
export const UserPlus = uicon('user-add', 'UserPlus');
export const UserRound = uicon('user', 'UserRound');
export const UserRoundPlus = uicon('user-add', 'UserRoundPlus');
export const Users = uicon('users', 'Users');
export const UsersRound = uicon('users-alt', 'UsersRound');
export const Video = uicon('video-camera-alt', 'Video');
export const Wrench = uicon('wrench-simple', 'Wrench');
export const X = uicon('cross-small', 'X');
export const Zap = uicon('bolt', 'Zap');
